/**
 * Variant Picker Tabs - JavaScript
 * Handles UK/US/EU tab switching and variant selection
 */

(function () {
  if (window.__GORAL_VARIANT_PICKER_TABS_INIT__) {
    return;
  }

  window.__GORAL_VARIANT_PICKER_TABS_INIT__ = true;

  class VariantPickerTabs extends HTMLElement {
    constructor() {
      super();

      this.onTabClick = this.onTabClick.bind(this);
      this.onRadioChange = this.onRadioChange.bind(this);
    }

    connectedCallback() {
      this.variantPicker = this.closest('variant-picker');
      this.formId = this.variantPicker?.getAttribute('form-id');
      this.form = this.formId ? document.getElementById(this.formId) || document.forms[this.formId] : null;
      this.tabs = Array.from(this.querySelectorAll('.variant-picker-tabs__tab'));
      this.selectedValueDisplay = this.closest('.variant-picker__option--size')?.querySelector('[data-selected-size]');
      this.radioInputs = Array.from(this.querySelectorAll('input[type="radio"]'));
      this.readLocationCode();
      // Set up tab click handlers
      this.tabs.forEach(tab => {
        tab.removeEventListener('click', this.onTabClick);
        tab.addEventListener('click', this.onTabClick);
      });

      // Set up size selection handlers
      this.radioInputs.forEach(radio => {
        radio.removeEventListener('change', this.onRadioChange);
        radio.addEventListener('change', this.onRadioChange);
        radio.addEventListener('change', () => {
          setTimeout(() => {
            window.sizeGuideTabs.init();
          }, 0);
        });
      });

      this.applyRegion(this.getPersistedRegionIndex());

      // Initialize display on load
      this.updateSelectedValueDisplay();
    }

    disconnectedCallback() {
      this.tabs?.forEach(tab => tab.removeEventListener('click', this.onTabClick));
      this.radioInputs?.forEach(radio => radio.removeEventListener('change', this.onRadioChange));
      this.radioInputs?.forEach((radio) => radio.removeEventListener('change', () => {
        setTimeout(() => {
          window.sizeGuideTabs.init();
        }, 0);
      }));
    }

    onTabClick(event) {
      this.switchRegion(event.currentTarget);
    }

    onRadioChange() {
      this.updateSelectedValueDisplay();
    }

    getPersistedRegionIndex() {
      const activeTab = this.querySelector('.variant-picker-tabs__tab.active');

      return this.form?.dataset.activeSizeRegionIndex
        || this.dataset.activeRegionIndex
        || activeTab?.dataset.regionIndex
        || '0';
    }

    persistRegionIndex(regionIndex) {
      this.dataset.activeRegionIndex = regionIndex;

      if (this.form) {
        this.form.dataset.activeSizeRegionIndex = regionIndex;
      }
    }

    applyRegion(regionIndex) {
      // Update active tab
      this.tabs.forEach(tab => tab.classList.remove('active'));

      const selectedTab = this.tabs.find(tab => tab.dataset.regionIndex === regionIndex) || this.tabs[0];
      const activeRegionIndex = selectedTab?.dataset.regionIndex || '0';

      selectedTab?.classList.add('active');

      // Show/hide appropriate size regions in all size boxes
      const allRegionSpans = this.querySelectorAll('.size-region');
      allRegionSpans.forEach(span => {
        if (span.dataset.regionIndex === activeRegionIndex) {
          span.style.display = 'block';
        } else {
          span.style.display = 'none';
        }
      });

      this.persistRegionIndex(activeRegionIndex);

      // Update the selected value display to show the size in the selected region
      this.updateSelectedValueDisplay();

      this.dispatchEvent(
        new CustomEvent('goral:size-regions-applied', { bubbles: true })
      );
    }

    switchRegion(selectedTab) {
      this.applyRegion(selectedTab.dataset.regionIndex);
    }

    readLocationCode() {
      const location = document.querySelector('product-rerender .product-info');

      if (!location) return;

      const isoCode = location.dataset.location;

      const location_eu = [
        'AX',
        'AD',
        'AT',
        'BE',
        'BG',
        'HR',
        'CY',
        'CZ',
        'DK',
        'EE',
        'FI',
        'FR',
        'DE',
        'GI',
        'GR',
        'HU',
        'IE',
        'IT',
        'XK',
        'LV',
        'LT',
        'LU',
        'MT',
        'MC',
        'ME',
        'NL',
        'NO',
        'PL',
        'PT',
        'RO',
        'SM',
        'SK',
        'SI',
        'ES',
        'SE',
        'CH',
        'VA',
      ];

      for (const tab of this.tabs) {
        if (tab?.dataset.isoCode === 'EU' && location_eu.includes(isoCode)) {
          this.switchRegion(tab);
          return;
        }
        if (
          (tab?.dataset.isoCode === 'US' && isoCode === 'US') ||
          (tab?.dataset.isoCode === 'US' && isoCode === 'CA')
        ) {
          this.switchRegion(tab);
          return;
        }
        if (tab?.dataset.isoCode === 'UK' && isoCode === 'GB') {
          this.switchRegion(tab);
          return;
        }
      }

      this.switchRegion(this.tabs[this.tabs.length - 1]);
    }

    updateSelectedValueDisplay() {
      if (!this.selectedValueDisplay) return;

      // Find the currently checked radio
      const checkedRadio = this.querySelector('input[type="radio"]:checked');
      if (!checkedRadio) return;

      // Get the full size value from data attribute (e.g., "UK7.5 / EU41.5 / US8.5")
      const fullSize = checkedRadio.dataset.fullSize;
      if (!fullSize) return;

      // Find which tab is currently active
      const activeTab = this.querySelector('.variant-picker-tabs__tab.active');
      if (!activeTab) return;

      const activeRegionIndex = activeTab.dataset.regionIndex;

      const regionPrefixes = {
        '0': 'UK',
        '1': 'US',
        '2': 'EU'
      };

      const targetPrefix = regionPrefixes[activeRegionIndex] || 'UK';
      const sizeParts = fullSize.split(' / ');
      const matchingPart = sizeParts.find(part => part.trim().startsWith(targetPrefix));
      const displaySize = matchingPart ? matchingPart.replace(targetPrefix, '').trim() : fullSize;

      this.selectedValueDisplay.textContent = displaySize;
    }
  }

  if (!customElements.get('variant-picker-tabs')) {
    customElements.define('variant-picker-tabs', VariantPickerTabs);
  }
})();
